import {
  Box,
  Button,
  VStack,
  Text,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  Icon,
  Tooltip,
  Select,
  Badge,
} from "@chakra-ui/react";
import { FaBook, FaBookmark, FaRegBookmark } from "react-icons/fa";
import { RiQuestionnaireLine } from 'react-icons/ri';
import { useState, useEffect, useContext, createContext, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import BasicInfo, { BasicInfoHandle } from "./BasicInfo";
import QuantityAndPrice, { QuantityAndPriceHandle } from "./QuantityAndPrice";
import PricingAgreementForm, { PricingAgreementFormHandle } from "./PricingAgreementForm";
import ProductionProcess from "./ProductionProcess";
import Equipment from "./Equipment";
import ProfitAndLoss, { ProfitAndLossHandle } from "./ProfitAndLoss";
import ProjectProfitablityStatement from "./ProjectProfitablityStatement";
//import { useUser } from "../contexts/UserContext";
//import { generateClient } from "aws-amplify/data";
//import type { Schema } from "../../amplify/data/resource";
//import { BsGear } from "react-icons/bs";

import {
  ProjectContextType,
  CurrencyFetchStatus,
  UserPermissionType,
  ReviewListItem,
  Currency,
  MonthlyRate,
} from "../../contexts/types";
import { ApplicationPathName } from '../../contexts/constants';
import { buildMonthlyRateMap } from "../../utils/reviewCalculation";
import Scheduling, { SchedulingHandle } from "./Scheduling";
import SimulationNewProduct from "./SimulationNewProduct";
import SimulationProductivityImprovement from "./SimulationProductivityImprovement";
import ReviewSales from "./ReviewSales";
import ReviewBusiness from "./ReviewBusiness";
import ReviewFuture, { ReviewFutureHandle } from "./ReviewFuture";
import { getReviewFlowDivisionPairs } from "../Approve/AssignTabModal";
//import ManufacturingProcess from "./components/ProductionProcess";
//import Setting from "./setting";
import { WebAPIContext } from '../../contexts/WebAPIContext';
// import FixedAsset from "./Setting/FixedAsset";
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { Affiliation, setEmployeeInfo, setIsAIRestricted } from '../../store/employeeSlice';
import Menu from '../../components/Menu';
import InvestmentScreeningDetailModal from "./InvestmentScreeningDetailModal";
import ReportAndDraft from "./ReportAndDraft";
// import CurrencyTestPage from "./CurrencyTestPage"; // ファイル未コミットのためコメントアウト
import { Messages, FlowKb, WorkflowId, ComfirmDivisions, SubWindowInvestmentScreeningDetailName, NewTabInvestmentScreeningDetailName, PurposeType, EquipmentUsageType, ValidPreviousStatus, isSimpleApplicationForm, ReviewPhasePresets, BookmarkMaxCount } from "../../contexts/constants";
import { PagePermissions } from "../../contexts/types";
import ConfirmModal from "../../components/ConfirmModal";
import ManualModal from "../../components/ManualModal";
import { getBookmarks, addBookmark, removeBookmark } from "../../utils/bookmarkStorage";
import { ViewRestrictionState, parseViewRestriction, needsApproverCheck, isRestrictedSalesUser, isScreenHidden, isProjectCopyRestricted } from "../../utils/viewRestriction";

// コンテキスト
export const ProjectContext = createContext<ProjectContextType | null>(null);

interface sendCancelMailResponse {
  data: {
    sendCancelMail: {
      result: string;
      failedAddresses?: string[];
      sentCount?: number;
    };
  };
}

const InvestmentScreeningDetail: React.FC = () => {

  const { t } = useTranslation();
  // ダイアログのタイトルを定義
  const TITLE = t('features.InvestmentScreening.InvestmentScreeningDetail.title');
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext)
  const [activeComponent, setActiveComponent] = useState<string>("BasicInfo");
  // 一時保存対応画面の ref（各画面の getSnapshot / applySnapshot を呼ぶためのエントリーポイント）
  const basicInfoRef = useRef<BasicInfoHandle | null>(null);
  const quantityAndPriceRef = useRef<QuantityAndPriceHandle | null>(null);
  const pricingAgreementFormRef = useRef<PricingAgreementFormHandle | null>(null);
  const schedulingRef = useRef<SchedulingHandle | null>(null);
  const profitAndLossRef = useRef<ProfitAndLossHandle | null>(null);
  const reviewFutureRef = useRef<ReviewFutureHandle | null>(null);
  // null になると一時保存/読み込みボタンが非表示になる（isCurrentTabEditable との AND 条件）
  const tempSaveRef =
    activeComponent === "BasicInfo" ? basicInfoRef :
      activeComponent === "QuantityAndPrice" ? quantityAndPriceRef :
        activeComponent === "PricingAgreementForm" ? pricingAgreementFormRef :
          activeComponent === "Scheduling" ? schedulingRef :
            activeComponent === "ProfitAndLoss" ? profitAndLossRef :
              activeComponent === "ReviewFuture" ? reviewFutureRef :
                null;

  // プロジェクト：目的
  const [purpose, setPurpose] = useState<string>(PurposeType.NewProduct.code);
  // プロジェクト：設備用途区分
  const [equipmentUsageType, setEquipmentUsageType] = useState<string>(EquipmentUsageType.Dedicated.code);
  // プロジェクト：申請書様式（detail/simple）
  const [applicationFormType, setApplicationFormType] = useState<string | undefined>(undefined);
  // プロジェクト：損益表示（show/hide）
  const [profitLossDisplayType, setProfitLossDisplayType] = useState<string | undefined>(undefined);

  // 振り返り準備OK
  const [isReadyReview, setIsReadyReview] = useState<boolean>(false);
  // initializeReviewIfNeeded の並行呼び出し防止フラグ
  const isInitializingReviewRef = useRef<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  // 振り返りモード管理
  // サブウィンドウモード判定（window.name と subWindowMode フラグの両方で確実に判定）
  const isSubWindow = window.name === SubWindowInvestmentScreeningDetailName
    && window.sessionStorage.getItem("subWindowMode") === "1";

  // 別タブモード判定（検索系画面からCtrl+クリック等で開いた独立編集可能タブ、window.name と newTabMode フラグの両方で確実に判定）
  const isNewTab = window.name === NewTabInvestmentScreeningDetailName
    && window.sessionStorage.getItem("newTabMode") === "1";

  // デバッグ用: localStorage.DEBUG === "true" のときのみ、URLクエリパラメータ ?reviewDate=YYYY-MM-DD でも振り返りモードに入れる
  const [reviewDate, setReviewDate] = useState<string | null>(() => {
    if (isSubWindow) {
      // サブウィンドウモード: subWindowReviewDate が唯一の正
      // nullなら申請データ表示（親の sessionStorage.reviewDate 継承を明確に遮断）
      return window.sessionStorage.getItem("subWindowReviewDate");
    }
    // if (isNewTab) {
    //   // 別タブモード: newTabReviewDate が唯一の正（親タブのreviewDateは参照しない）
    //   return window.sessionStorage.getItem("newTabReviewDate");
    // }
    // 通常モード: 従来通り
    const stored = sessionStorage.getItem("reviewDate");
    const state = location.state?.reviewDate;
    const isDebug = localStorage.getItem("DEBUG") === "true";
    const queryReviewDate = isDebug
      ? new URLSearchParams(window.location.search).get("reviewDate")
      : null;
    return stored || state || queryReviewDate || null;
  });
  const isReviewMode = reviewDate !== null;
  const [reviewList, setReviewList] = useState<ReviewListItem[]>([]);
  const [monthlyRateMap, setMonthlyRateMap] = useState<Map<string, Currency[]>>(new Map());
  // 記入者ユーザーIDを管理
  const [registrantEmployeeId, setRegistrantEmployeeId] = useState<string>("");
  const [registrantDivisionId, setRegistrantDivisionId] = useState<string | null>(null);
  /** 画面閲覧制限の判定状態（undefined = 未取得。未取得の間は制限可能画面を隠す） */
  const [viewRestrictionState, setViewRestrictionState] = useState<ViewRestrictionState>(undefined);
  /** 画面閲覧制限の判定が済んだか。済むまでサイドバーのメニューとメイン画面を表示しない（取得失敗時も済み扱いにし、制限側で表示する） */
  const [isViewRestrictionResolved, setIsViewRestrictionResolved] = useState<boolean>(false);
  // 月別レート取得の期間計算用
  //   periodStart: min(現在PJ開始, 過去PJ開始) の早い方
  //   → 過去PJ設備の疑似検収月が現在PJ開始より前になるケースをカバー
  const [projectStartDate, setProjectStartDate] = useState<string>("");
  const [projectEndDate, setProjectEndDate] = useState<string | null>(null);
  const [pastProjectStartDate, setPastProjectStartDate] = useState<string>("");

  const { email } = useAppSelector((state) => state.auth); // ログインユーザのemailを取得
  const employeeId = useAppSelector((state) => state.employee.employeeId);
  const employeeName = useAppSelector((state) => state.employee.employeeName);
  const divisionId = useAppSelector((state) => state.employee.divisionId);
  const divisionName = useAppSelector((state) => state.employee.divisionName);
  const affiliations = useAppSelector((state) => state.employee.affiliations);
  const dispatch = useAppDispatch();

  // 複数所属の部署名を「/」区切りで結合
  const allDivisionNames = affiliations?.map(a => a.organizationName).filter(Boolean).join(" / ") || divisionName;

  // Modalの制御
  const {
    isOpen: isOpenInvestmentScreeningDetailModal,
    onOpen: inOpenInvestmentScreeningDetailModal,
    onClose: onCloseInvestmentScreeningDetailModal
  } = useDisclosure();

  const openInvestmentScreeningDetailModal = () => {
    inOpenInvestmentScreeningDetailModal();
  };

  // 取消確認モーダルの開閉状態を管理するフック
  const { isOpen, onOpen, onClose } = useDisclosure();

  /** 案件コピー作成確認モーダル用 */
  const { isOpen: isOpenCopyProjectConfirm, onOpen: onOpenCopyProjectConfirm, onClose: onCloseCopyProjectConfirm } = useDisclosure();

  /** マニュアルモーダル用 */
  const { isOpen: isManualModalOpen, onOpen: onManualModalOpen, onClose: onManualModalClose } = useDisclosure();

  /**
   * FAQアイコン押下時の処理
   * 別タブで開き、既存のFAQタブのある時はそのタブをアクティブにする
   */
  const handleOpenFAQ = () => {
    const faqWindow = window.open(ApplicationPathName.Faq.Url, 'FAQ');
    // 既存でFAQタブのある時、そのタブをフォーカスする
    if (faqWindow) {
      faqWindow.focus();
    }
  };

  // ========== DEBUG: 月別レート一覧モーダル（テスト用・後で削除） ==========
  const { isOpen: isRateDebugModalOpen, onOpen: onRateDebugModalOpen, onClose: onRateDebugModalClose } = useDisclosure();
  // ========== DEBUG END ==========

  // 前画面からprojectIdのパラメータを取得
  // const stateProjectId = location.state?.projectId || "";
  // ProjectId設定
  const [projectId, setProjectId] = useState<string>(() => {
    const stored = sessionStorage.getItem("projectId");
    const state = location.state?.projectId;
    return stored || state || "";
  });

  // ブックマーク登録済みか（projectId/reviewDate変更時に再判定）
  const [isBookmarked, setIsBookmarked] = useState<boolean>(false);

  const [userPermissions, setUserPermissions] = useState<UserPermissionType[]>([]);
  // 通貨マスタ。各タブ個別取得の重複を避けるため、ここで一度だけ取得しContext経由で配布する
  const [currencyData, setCurrencyData] = useState<Currency[]>([]);
  // 通貨マスタ取得完了フラグ（取得完了前は各タブの初期表示による通貨変換ミスを防ぐため描画をゲートする）
  const [isCurrencyLoaded, setIsCurrencyLoaded] = useState<boolean>(false);
  const [currencyStatus, setCurrencyStatus] = useState<CurrencyFetchStatus | undefined>(undefined);

  // ブックマーク登録済み判定（projectId/reviewDate変更時に再判定）
  useEffect(() => {
    if (!projectId) {
      setIsBookmarked(false);
      return;
    }
    (async () => {
      const bookmarks = await getBookmarks();
      setIsBookmarked(bookmarks.some(b => b.projectId === projectId && (b.reviewDate || null) === (reviewDate || null)));
    })();
  }, [projectId, reviewDate]);

  // 現在アクティブなタブが編集可能か（入力不可時は一時保存/読み込みボタンを非表示にするために使用）
  const isCurrentTabEditable = useMemo(() => {
    // 新規作成（userPermissions未取得）は全画面編集可
    if (!userPermissions || userPermissions.length === 0) return true;
    const myPermission = userPermissions.find(p => p.employeeId === employeeId);
    const currentFlowId = myPermission?.flowId || userPermissions[0]?.flowId;
    // 承認完了・取消は全画面編集不可
    if (currentFlowId === WorkflowId.ApproveComplete || currentFlowId === WorkflowId.Cancel) return false;
    // 差し戻しはBasicInfoのみ編集可
    const activeFlowId = WorkflowId[activeComponent as keyof typeof WorkflowId];
    if (currentFlowId === WorkflowId.Remand && activeFlowId !== WorkflowId.BasicInfo) return false;
    return true;
  }, [userPermissions, employeeId, activeComponent]);

  // 承認完了または取消済みか（取消ボタンの表示制御に使用）
  const isApprovalFinished = useMemo(() => {
    if (!userPermissions || userPermissions.length === 0) return false;
    const myPermission = userPermissions.find(p => p.employeeId === employeeId);
    const currentFlowId = myPermission?.flowId || userPermissions[0]?.flowId;
    return currentFlowId === WorkflowId.ApproveComplete || currentFlowId === WorkflowId.Cancel;
  }, [userPermissions, employeeId]);

  // 現在のタブの画面名（一時保存/読み込みトーストのタイトルに使用）
  const activeComponentName = useMemo(() => {
    const base = 'features.InvestmentScreening.InvestmentScreeningDetail.section.';
    const map: Record<string, string> = {
      BasicInfo: 'basicInfo',
      QuantityAndPrice: equipmentUsageType === EquipmentUsageType.GeneralPurpose.code ? 'assumedQuantityAndPrice' : 'salesQuantityAndPrice',
      PricingAgreementForm: 'pricingAgreement',
      Scheduling: 'schedule',
      ProfitAndLoss: 'cost',
      ReviewFuture: 'reviewFuture',
    };
    const key = map[activeComponent];
    return key ? t(base + key) : activeComponent;
  }, [activeComponent, equipmentUsageType, t]);


  // 画面を開くたびにAI制限フラグを再チェック
  useEffect(() => {
    if (!email) return;
    const refreshLoginUserInfo = async () => {
      try {
        const result = await WebAPI.request({
          query: `
            query getLoginUserInfo($userId: String!) {
              getLoginUserInfo(userId: $userId) {
                userId
                userName
                userRole
                isMasterMaintenanceAccessible
                isHeadquartersManagement
                isAIEnabled
                affiliations {
                  organizationId
                  organizationName
                  positionId
                  positionName
                  order
                  parentOrganizationId
                  descendantOrganizationClassIds
                }
              }
            }
          `,
          variables: { userId: email },
        });
        const info = result?.data?.getLoginUserInfo;
        if (!info) return;
        const primaryAffiliation = info.affiliations?.[0];
        dispatch(setEmployeeInfo({
          employeeId: info.userId,
          employeeName: info.userName || "",
          divisionId: primaryAffiliation?.organizationId || "",
          divisionName: primaryAffiliation?.organizationName || "",
          parentOrganizationId: primaryAffiliation?.parentOrganizationId || "",
          descendantOrganizationClassIds: primaryAffiliation?.descendantOrganizationClassIds || [],
          affiliations: info.affiliations || [],
          userRole: info.userRole || null,
          isMasterMaintenanceAccessible: info.isMasterMaintenanceAccessible || false,
          isHeadquartersManagement: info.isHeadquartersManagement || false,
          isAIEnabled: info.isAIEnabled ?? false,
        }));
      } catch (error) {
        console.error("refreshLoginUserInfo error:", error);
      }
    };
    refreshLoginUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // projectIdが変更されたときにBasicInfoコンポーネントを表示
  useEffect(() => {
    if (projectId) {
      // 振り返りモード時は振り返りデータ準備前に参照系APIが走り Forbidden となるため、
      // データ取得は initializeReviewIfNeeded 内のデータ準備後に行う
      if (!reviewDate) {
        fetchData();
      }

      // projectIdをセッションストレージに保存
      sessionStorage.setItem("projectId", projectId);

      // 振り返り一覧を取得（スケジュール画面で振り返り済み判定に使用）
      fetchReviewList(projectId);
    } else {
      // 新規投資申請時は案件紐付けの通貨マスタが存在せず fetchData も走らないため、
      // 描画ゲート（isCurrencyLoaded）を解放して初期表示（基本情報画面）を可能にする
      setIsCurrencyLoaded(true);
    }
    // 画面閲覧制限: 既存案件は判定データ取得まで未取得扱い。新規作成は起案者本人のため制限なし
    setViewRestrictionState(projectId ? undefined : { restriction: null, isRestrictedUser: false });
    setIsViewRestrictionResolved(!projectId);
    setActiveComponent("BasicInfo");
  }, [projectId]);

  // reviewDateが変更されたときにsessionStorageに保存・データ再取得
  useEffect(() => {
    if (reviewDate) {
      // 振り返りごとに制限設定が異なるため、判定データを取り直すまで未取得扱いに戻す
      // （申請データに戻る場合は、切替処理側で戻す）
      setViewRestrictionState(undefined);
      setIsViewRestrictionResolved(false);
      sessionStorage.setItem("reviewDate", reviewDate);
      if (projectId) {
        initializeReviewIfNeeded(projectId, reviewDate);
      }
    } else {
      sessionStorage.removeItem("reviewDate");
    }
  }, [reviewDate]);

  // 画面閲覧制限: 表示中の画面が隠す対象になったら基本情報に戻す
  useEffect(() => {
    if (isScreenHidden(WorkflowId[activeComponent as keyof typeof WorkflowId], viewRestrictionState)) {
      setActiveComponent("BasicInfo");
    }
  }, [activeComponent, viewRestrictionState]);

  // 振り返りモード: reviewDate が設定されたら月別為替レートを取得
  // 期間:
  //   - periodStart: min(現在PJ開始, 過去PJ開始) の早い方（過去PJ設備の疑似検収月をカバー）
  //   - periodEnd: 振り返り時点（reviewDate）
  //   → これより後の月は reviewCalculation.getRatesForMonth でフォールバック（申請時レート）が使われる
  useEffect(() => {
    if (!reviewDate) {
      setMonthlyRateMap(new Map());
      return;
    }
    // projectStartDate が未取得の場合はスキップ（取得後に再実行される）
    if (!projectStartDate) {
      return;
    }

    const fetchMonthlyRates = async () => {
      try {
        // "YYYY-MM-DD" → "YYYY-MM"
        const currentStartMonth = projectStartDate.substring(0, 7);
        const pastStartMonth = pastProjectStartDate ? pastProjectStartDate.substring(0, 7) : "";
        // 過去PJ開始月があれば早い方を採用、なければ現在PJ開始月
        const periodStart = pastStartMonth
          ? [currentStartMonth, pastStartMonth].sort()[0]
          : currentStartMonth;
        const periodEnd = reviewDate.substring(0, 7);

        const payload = {
          query: `
            query getCurrencyByPeriod($periodStart: String!, $periodEnd: String!) {
              getCurrencyByPeriod(periodStart: $periodStart, periodEnd: $periodEnd) {
                currencyId
                currencyName
                exchangeRate
                rateMonth
              }
            }`,
          variables: { periodStart, periodEnd }
        };
        const response = await WebAPI.request(payload);
        if (response?.data?.getCurrencyByPeriod) {
          setMonthlyRateMap(buildMonthlyRateMap(response.data.getCurrencyByPeriod));
        }
      } catch (error) {
        console.error("月別為替レート取得エラー:", error);
      }
    };
    fetchMonthlyRates();
  }, [reviewDate, projectStartDate, pastProjectStartDate]);

  let editProject = false;
  // if (stateProjectId !== "") { editProject = true; }
  if (projectId !== "") { editProject = true; }

  const [editExistingProject, setEditExistingProject] = useState(editProject);

  // プロジェクトID更新用コールバック関数
  const handleProjectIdChange = (newProjectId: string) => {
    setProjectId(newProjectId);
  };

  //  console.log("basicinfo", projectBasicInfo)

  // コンポーネントがマウントされたときに初期化
  /*
  useEffect(() => {
    console.log("location.state.projectId:" + location.state.projectId);
    if (!location.state?.projectId) {
      setProjectId(location.state.projectId);
      setEditExistingProject(false);
      setUserPermissions([]);
    }
  }, [location.key]);
  */



  /**
   * データ取得
   * @param reviewDateArg 振り返り日
   */
  const fetchData = async (reviewDateArg?: string | null) => {
    try {
      const reviewDateLocal = reviewDateArg === undefined ? reviewDate : reviewDateArg;

      const payload = {
        query: `
          query getUserPermissions($projectId: String, $reviewDate: String) {
            getUserPermissions(projectId: $projectId, reviewDate: $reviewDate) {
              employeeId
              flowKb
              flowId
              divisionId
            }
          }`,
        variables: {
          projectId: projectId,
          ...(reviewDateLocal && { reviewDate: reviewDateLocal })
        }
      };

      // 権限情報・通貨マスタ・プロジェクト情報は互いに依存しないため並行で取得する
      // （画面閲覧制限の判定が済むまでメニューを出さないため、待ち時間を短くする）
      const permissionsPromise = WebAPI.request(payload);
      const currencyPromise = projectId
        ? WebAPI.request({
          query: `
            query getCurrencyById($projectId: String) {
              getCurrencyById(projectId: $projectId) {
                currencyId
                currencyName
                exchangeRate
                order
                updateDate
              }
            }`,
          variables: { projectId: projectId }
        }).catch((currencyError) => {
          console.error(`getCurrencyById error`, currencyError);
          return undefined;
        })
        : undefined;
      const projectInfoPromise = projectId ? getProjectById(projectId, reviewDateLocal) : undefined;

      // 権限情報取得
      const response = await permissionsPromise;

      if (response?.data?.getUserPermissions) {
        const userPermissions: UserPermissionType[] = response?.data?.getUserPermissions;
        // 絞り込み確認用
        // const filteredPermissions = userPermissions.filter(permission => permission.flowId === 4);
        setUserPermissions(userPermissions);

      } else {
        console.error(`getUserPermissions fail`, response.errors)
      }

      // 通貨マスタ取得（各タブはContext経由でこの結果を参照し、個別取得は行わない）
      try {
        // 新規案件での処理
        if (!projectId) {
          return;
        }
        const currencyResponse = await currencyPromise;
        const fetchedCurrency = currencyResponse?.data?.getCurrencyById;
        if (Array.isArray(fetchedCurrency)) {
          setCurrencyData(fetchedCurrency);
          // 0件は基本情報未保存（案件コピー直後など）、取得失敗とは区別する
          setCurrencyStatus(fetchedCurrency.length === 0 ? 'empty' : 'ok');
        } else {
          setCurrencyStatus('error');
          console.error(`getCurrencyById fail`, currencyResponse?.errors);
        }
      } catch (currencyError) {
        setCurrencyStatus('error');
        console.error(`getCurrencyById error`, currencyError);
      } finally {
        setIsCurrencyLoaded(true);
      }

      // プロジェクト情報を取得
      const projectInfo = await projectInfoPromise;
      // 画面閲覧制限の判定（失敗時は状態を更新しない＝未取得または前回値のまま）
      await resolveViewRestriction(projectInfo, reviewDateLocal);
      setIsViewRestrictionResolved(true);
      if (projectInfo.registrantEmployeeId) {
        // 記入者ユーザーIDが取得できら state を更新
        setRegistrantEmployeeId(projectInfo.registrantEmployeeId);
      }
      if (projectInfo.registrantDivisionId) {
        setRegistrantDivisionId(projectInfo.registrantDivisionId);
      }
      // 申請部署に基づくAI制限フラグをプロジェクトごとに更新
      dispatch(setIsAIRestricted(projectInfo.isAIRestricted ?? false));
      if (projectInfo.purpose) {
        // プロジェクト：目的
        setPurpose(projectInfo.purpose);
      }
      if (projectInfo.equipmentUsageType) {
        // プロジェクト：設備用途区分
        setEquipmentUsageType(projectInfo.equipmentUsageType);
      }
      // 申請書様式（合理化・更新案件のみ意味あり。未設定はdetail扱い）
      setApplicationFormType(projectInfo.applicationFormType ?? "detail");
      // 損益表示（簡易様式のみ意味あり。未設定はshow扱い）
      setProfitLossDisplayType(projectInfo.profitLossDisplayType ?? "show");
      if (projectInfo.projectStartDate) {
        // 月別レート取得の期間計算用
        setProjectStartDate(projectInfo.projectStartDate);
      }
      setProjectEndDate(projectInfo.projectEndDate ?? null);
      // 過去PJがある場合は過去PJ開始日も取得（periodStartの早い方を採用）
      if (projectInfo.pastProjectId) {
        try {
          const pastResponse = await WebAPI.request({
            query: `
              query getProjectById($projectId: String, $useRDSData: Boolean, $referencingProjectId: String) {
                getProjectById(projectId: $projectId, useRDSData: $useRDSData, referencingProjectId: $referencingProjectId) {
                  projectStartDate
                }
              }`,
            variables: { projectId: projectInfo.pastProjectId, useRDSData: true, referencingProjectId: projectId }
          });
          const pastStart = pastResponse?.data?.getProjectById?.projectStartDate;
          if (pastStart) {
            setPastProjectStartDate(pastStart);
          } else {
            setPastProjectStartDate("");
          }
        } catch (e) {
          console.warn("過去PJ開始日取得失敗:", e);
          setPastProjectStartDate("");
        }
      } else {
        setPastProjectStartDate("");
      }

    } catch (error) {
      console.error("データ取得エラー:", error);
    } finally {
      // 取得に失敗してもメニューが出ないままにならないよう、判定済みにする（判定データがなければ制限側で表示）。
      // 新規案件（projectId なし）は最初から判定済みのため触らない
      if (projectId) {
        setIsViewRestrictionResolved(true);
      }
    }
  };

  /**
   * 画面閲覧制限の判定状態を更新する
   * @param projectInfo getProjectById の結果
   * @param reviewDateLocal 振り返り日
   */
  const resolveViewRestriction = async (
    projectInfo: {
      screenViewRestriction?: string | null;
      registrantEmployeeId?: string | null;
      registrantDivisionId?: string | null;
      confirmEmployees?: { employeeId: string; divisionId: string }[] | null;
    } | undefined,
    reviewDateLocal: string | null
  ) => {
    if (!projectInfo) return;
    try {
      const restriction = parseViewRestriction(projectInfo.screenViewRestriction);
      const userArgs = {
        loginEmployeeId: employeeId,
        loginAffiliationOrgIds: affiliations?.map(a => a.organizationId) || [divisionId],
        registrantEmployeeId: projectInfo.registrantEmployeeId,
        registrantDivisionId: projectInfo.registrantDivisionId,
        confirmEmployees: projectInfo.confirmEmployees ?? [],
      };

      // 承認者判定は、他の条件で制限対象外と決まらないときだけ行う
      let approverEmployeeIds: string[] = [];
      if (needsApproverCheck({ ...userArgs, restriction })) {
        const response = await WebAPI.request({
          query: `
            query getApproverList($projectId: String, $divisionId: String, $reviewDate: String) {
              getApproverList(projectId: $projectId, divisionId: $divisionId, reviewDate: $reviewDate) {
                employeeId
              }
            }`,
          variables: {
            projectId: projectId,
            // divisionId は必須だが検索条件には使われない
            divisionId: ComfirmDivisions.salesDivision,
            ...(reviewDateLocal && { reviewDate: reviewDateLocal })
          }
        });
        const approvers = response?.data?.getApproverList;
        if (!Array.isArray(approvers)) {
          throw new Error(`getApproverList fail: ${JSON.stringify(response?.errors)}`);
        }
        approverEmployeeIds = approvers.map((a: { employeeId: string }) => a.employeeId);
      }

      setViewRestrictionState({
        restriction,
        isRestrictedUser: isRestrictedSalesUser({ ...userArgs, approverEmployeeIds }),
      });
    } catch (error) {
      console.error("画面閲覧制限の判定エラー:", error);
    }
  };

  // プロジェクト情報取得API
  const getProjectById = async (projectId: string, reviewDateArg: string | null = reviewDate) => {
    const functionName = 'getProjectById';
    try {
      console.log(`${functionName} START`);
      const response = await WebAPI.request({
        query: `
          query ${functionName}($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
            ${functionName}(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
              registrantEmployeeId
              registrantDivisionId
              purpose
              applicationFormType
              profitLossDisplayType
              equipmentUsageType
              projectStartDate
              projectEndDate
              pastProjectId
              isAIRestricted
              screenViewRestriction
              confirmEmployees {
                employeeId
                divisionId
              }
            }
          }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          ...(reviewDateArg && { reviewDate: reviewDateArg })
        }
      });
      console.log(`${functionName} RESPONSE:`, response);
      return response?.data?.getProjectById;
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return undefined;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 振り返り一覧取得API
  const fetchReviewList = async (projectId: string) => {
    const functionName = 'getReviewListByProjectId';
    try {
      console.log(`${functionName} START`);
      const response = await WebAPI.request({
        query: `
          query ${functionName}($projectId: String!) {
            ${functionName}(projectId: $projectId) {
              reviewDate
              reviewPhase
              createDate
            }
          }`,
        variables: { projectId }
      });
      console.log(`${functionName} RESPONSE:`, response);
      if (response?.data?.getReviewListByProjectId) {
        setReviewList(response.data.getReviewListByProjectId);
        return response.data.getReviewListByProjectId as ReviewListItem[];
      }
      return [];
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return [];
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 振り返りデータ初期化（未登録時にコピー実行）
  const initializeReviewIfNeeded = async (projectId: string, reviewDate: string) => {
    const functionName = 'initializeReview';

    // 並行呼び出し防止: 既に初期化中なら何もしない
    if (isInitializingReviewRef.current) {
      console.log(`${functionName}: 既に初期化処理中のためスキップ`);
      return;
    }
    isInitializingReviewRef.current = true;

    try {
      console.log(`${functionName} START`);
      // 振り返りデータの存在確認
      const checkResponse = await WebAPI.request({
        query: `
          query checkReviewExists($projectId: String!, $reviewDate: String!) {
            checkReviewExists(projectId: $projectId, reviewDate: $reviewDate)
          }`,
        variables: { projectId, reviewDate }
      });

      const exists = checkResponse?.data?.checkReviewExists;
      // exists === false のときのみ未登録と判断する（undefined/null はエラー扱いでスキップ）
      if (exists === false) {
        // 未登録: 申請データをコピー
        console.log(`${functionName}: 振り返りデータ未登録。コピーを実行します。`);
        // 振り返り3画面（flowIds 13/14/15）の (flowId, divisionId) ペアを BE に送信
        // BE の _copyWorkflow で 1回目コピー時に同 divisionId の担当者を推論し、
        // r_ceas_review_workflow に派生レコードを生成する
        const reviewFlowDivisionPairs = getReviewFlowDivisionPairs();
        const initResponse = await WebAPI.request({
          query: `
            mutation ${functionName}($input: InitializeReviewInput!) {
              ${functionName}(input: $input) { status message }
            }`,
          variables: {
            input: { projectId, reviewDate, reviewFlowDivisionPairs }
          }
        });
        console.log(`${functionName} RESULT:`, initResponse);
        const reviewStatus = initResponse?.data?.initializeReview?.status;
        // 順序ブロック（より古い未完了の振り返りが存在する場合）は warning toast で専用メッセージ表示
        if (reviewStatus === 'olderReviewPending') {
          console.log(`${functionName}: olderReviewPending by BE`);
          toast({
            title: t('features.InvestmentScreening.InvestmentScreeningDetail.title'),
            description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.reviewOlderPending'),
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          // 振り返りデータが作成されず画面を表示できないため、ホーム画面に戻す
          navigate("/");
          return;
        }
        if (reviewStatus !== 'success') {
          toast({
            title: t('features.InvestmentScreening.InvestmentScreeningDetail.title'),
            description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.reviewInitFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          navigate("/");
          return;
        }
        // 振り返り一覧を再取得してフェーズ情報を取得
        const updatedList = await fetchReviewList(projectId);
        const currentReview = updatedList.find(r => r.reviewDate === reviewDate);
        const phaseName = (currentReview?.reviewPhase
          ? ReviewPhasePresets.find(p => p.value === currentReview.reviewPhase)?.label ?? currentReview.reviewPhase
          : null) || t('features.InvestmentScreening.InvestmentScreeningDetail.label.reviewDefault');
        toast({
          title: t('features.InvestmentScreening.InvestmentScreeningDetail.title'),
          description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.reviewStart', { phaseName, reviewDate }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else if (exists === undefined || exists === null) {
        // checkReviewExists の通信エラー: 安全のため初期化をスキップ
        console.warn(`${functionName}: checkReviewExists が undefined/null を返したため初期化をスキップ`);
        return;
      } else {
        // 登録済み: reviewList を取得（申請データに戻るSelectの表示に必要）
        await fetchReviewList(projectId);
      }
      // データ再取得
      await fetchData();

      // 振り返り準備完了
      setIsReadyReview(true);

      // 子コンポーネント（BasicInfo等）にデータ再取得を促すため、一旦アンマウント→再マウント
      setActiveComponent("");
      setTimeout(() => setActiveComponent("BasicInfo"), 0);
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
    } finally {
      isInitializingReviewRef.current = false;
      console.log(`${functionName} END`);
    }
  };

  // ワークフロー更新(取消)API
  const putCancel = async (): Promise<string> => {
    const functionName = 'putCancel';
    try {
      console.log(`${functionName} START`);
      const input = {
        projectId: projectId,
        employeeId: employeeId,
        divisionId: divisionId,
        ...(reviewDate && { reviewDate }),
      };
      const query = `
        mutation ${functionName} ($input: CancelInput!) {
          ${functionName}(input: $input) {status message}
        }
      `;
      const result = await WebAPI.request({
        query: query,
        variables: { input }
      });
      console.log(`${functionName} RESULT:`, result);
      return result?.data?.putCancel?.status;
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return `error`;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 取消メール送信API
  const sendCancelMail = async (projectId: string): Promise<{ success: boolean; failedAddresses?: string[]; sentCount?: number }> => {
    const functionName = 'sendCancelMail';
    try {
      console.log(`${functionName} START`);
      console.info(`projectId: ${projectId}`);
      const result: sendCancelMailResponse = await WebAPI.request({
        query: `
          mutation ${functionName}(
            $projectId: String,
            $reviewDate: String
          ) {
            ${functionName}(
              projectId: $projectId,
              reviewDate: $reviewDate
            ) {
              result
              failedAddresses
              sentCount
            }
          }
        `,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });
      console.log(`${functionName} RESULT:`, result);
      const mailResult = result?.data?.sendCancelMail;
      return { success: mailResult?.result === 'true', failedAddresses: mailResult?.failedAddresses, sentCount: mailResult?.sentCount };
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return { success: false };
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 取消ボタン押下処理
  const onCancel: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    const functionName = 'onCancel';
    try {
      console.log(`${functionName} START`);
      // ボタンのフォーカスを外す
      event.currentTarget.blur();
      // ワークフロー更新(取消)
      const status = await putCancel();
      if (status === `success`) {
        // 成功
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelSuccess'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // 失敗
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // 取消メール送信APIを実行
      const cancelMailResult = await sendCancelMail(projectId);
      if (!cancelMailResult.success) {
        const sentPart = (cancelMailResult.sentCount || 0) > 0
          ? t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelMailSentPart', { count: cancelMailResult.sentCount })
          : '';
        const failedDesc = cancelMailResult.failedAddresses?.length
          ? t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelMailFailedDesc', { sentPart, failedAddresses: cancelMailResult.failedAddresses.join(', ') })
          : t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelMailAllFailed');
        toast({
          title: t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelMailFailedTitle'),
          description: failedDesc,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelMailSuccessTitle'),
          description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.cancelMailSuccess'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      // ホーム画面に遷移（メール送信失敗でも取消自体は成功しているため遷移）
      navigate("/")
    } catch (error) {
      // エラー
      toast({
        title: t('common.label.failed'),
        description: t('common.api.commonError'),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error(`${functionName} ERROR:`, error);
      return;
    } finally {
      console.log(`${functionName} END`);
      // 取消確認モーダルを閉じる
      onClose();
    }
  };

  /**
   * 「案件コピー作成」ボタン押下処理
   */
  const handleCopyProject = async () => {
    const functionName = 'handleCopyProject';

    try {
      console.log(`${functionName} START`);

      // 登録パラメータ
      const input = {
        sourceProjectId: projectId,
        registrantEmployeeId: employeeId,
        registrantDivisionId: divisionId,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation copyProject($input: CopyProjectInput!) {
              copyProject(input: $input) {status, message}
            }
          `,
        variables: {
          input: input,
        }
      });

      console.log(`${functionName} response = `, result);

      if (result.data.copyProject.status === "success") {
        const newProjectInfo = JSON.parse(result.data.copyProject.message);

        toast({
          title: TITLE,
          description: (
            <>
              {t('features.InvestmentScreening.InvestmentScreeningDetail.message.copySuccessLine1')}<br />
              {t('features.InvestmentScreening.InvestmentScreeningDetail.message.copySuccessLine2', { projectName: newProjectInfo?.projectName })}<br />
              {t('features.InvestmentScreening.InvestmentScreeningDetail.message.copySuccessLine3')}
            </>
          ),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // コピー完了後にホーム画面へ遷移
        navigate("/");
      } else if (result.data.copyProject.status === "removed") {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.copyRemoved'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else if (result.data.copyProject.status === "partial") {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.copyPartialFailed'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.copyFailed'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error(`${functionName} ERROR`, error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.copyExecutionFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      console.log(`${functionName} END`);
    }
  }

  //  // ========== saveProjectStatus (既存 or 新規の CapexCurrentProjectStatus を update/create) ==========
  //  const saveProjectStatus = async (newProjectId: string) => {
  //    try {
  //      // email で 1件取得
  //      const listResponse = await client.models.CapexCurrentProjectStatus.list({
  //        filter: { email: { eq: email } },
  //        limit: 1,
  //      });
  //
  //      if (listResponse?.data && listResponse.data.length > 0) {
  //        // 既存レコードあり → update
  //        const existingRecord = listResponse.data[0];
  //        const updateResponse = await client.models.CapexCurrentProjectStatus.update({
  //          id: existingRecord.id,
  //          ProjectId: newProjectId,
  //          email: email,
  //        } as any);
  //
  //        if (updateResponse?.data) {
  //          console.log("更新成功:", updateResponse.data);
  //        } else {
  //          console.error("更新失敗: レスポンスデータなし");
  //        }
  //      } else {
  //        // レコードなし → create
  //        const createResponse = await client.models.CapexCurrentProjectStatus.create({
  //          ProjectId: newProjectId,
  //          email: user.email,
  //        } as any);
  //
  //        if (createResponse?.data) {
  //          console.log("新規作成成功:", createResponse.data);
  //        } else {
  //          console.error("新規作成失敗: レスポンスデータなし");
  //        }
  //      }
  //    } catch (error) {
  //      console.error("CapexCurrentProjectStatus の保存/更新に失敗:", error);
  //    }
  //  };

  // ========== renderComponent ==========
  const renderComponent = () => {
    // 画面閲覧制限で隠す画面は描画しない（サイドバー非表示の保険）
    if (isScreenHidden(WorkflowId[activeComponent as keyof typeof WorkflowId], viewRestrictionState)) {
      return null;
    }
    switch (activeComponent) {
      case "BasicInfo":
        return (
          <BasicInfo
            ref={basicInfoRef}
            projectId={projectId}
            onProjectIdChange={handleProjectIdChange}
            refreshUserPermissions={fetchData}
            isReadyReview={isReadyReview}
          />
          //          <BasicInfo
          //            fetchProjectIdFromStatus={fetchProjectIdFromStatus}
          //            projectBasicInfo={projectBasicInfo}
          //            setProjectBasicInfo={setProjectBasicInfo}
          //            editExistingProject={editExistingProject}
          //            saveProjectStatus={saveProjectStatus}
          //          />
        );
      case "QuantityAndPrice":
        return (
          <QuantityAndPrice
            ref={quantityAndPriceRef}
            projectId={projectId}
            equipmentUsageType={equipmentUsageType}
            initialPurpose={purpose}
            initialApplicationFormType={applicationFormType}
          />
        );
      case "PricingAgreementForm":
        return (
          <PricingAgreementForm
            ref={pricingAgreementFormRef}
            projectId={projectId}
          />
        );
      case "Scheduling":
        return (
          <Scheduling
            ref={schedulingRef}
            projectId={projectId}
            reviewList={reviewList}
          />
        );
      //      case "ManufacturingProcess":
      //        return (
      //          <ManufacturingProcess
      //            fetchProjectIdFromStatus={fetchProjectIdFromStatus}
      //            projectBasicInfo={projectBasicInfo}
      //            editExistingProject={editExistingProject}
      //          />
      //        );
      case "ProductionProcess":
        return (
          <ProductionProcess
            projectId={projectId}
            monthlyRateMap={monthlyRateMap}
          />
        );
      case "Equipment":
        return (
          <Equipment
            projectId={projectId}
            equipmentUsageType={equipmentUsageType}
          />
        );
      case "ProfitAndLoss":
        return (
          <ProfitAndLoss
            ref={profitAndLossRef}
            projectId={projectId}
            initialPurpose={purpose}
            initialApplicationFormType={applicationFormType}
          />
        );
      case "ProjectProfitablityStatement":
        return (
          <ProjectProfitablityStatement
            projectId={projectId}
            reviewDate={reviewDate}
            monthlyRateMap={monthlyRateMap}
            initialPurpose={purpose}
            initialApplicationFormType={applicationFormType}
            initialProfitLossDisplayType={profitLossDisplayType}
          />
        );
      case "SimulationNewProduct":
        return (
          <SimulationNewProduct
            projectId={projectId}
            monthlyRateMap={monthlyRateMap}
          />
        );
      case "SimulationProductivityImprovement":
        return (
          <SimulationProductivityImprovement
            projectId={projectId}
            monthlyRateMap={monthlyRateMap}
            initialPurpose={purpose}
            initialApplicationFormType={applicationFormType}
            initialProfitLossDisplayType={profitLossDisplayType}
          />
        );
      case "ReviewSales":
        return (
          <ReviewSales
            projectId={projectId}
            reviewDate={reviewDate}
            monthlyRateMap={monthlyRateMap}
            isSimpleMode={isSimpleApplicationForm(purpose, applicationFormType)}
            projectEndDate={projectEndDate}
          />
        );
      case "ReviewBusiness":
        return (
          <ReviewBusiness
            projectId={projectId}
            reviewDate={reviewDate}
            projectEndDate={projectEndDate}
          />
        );
      case "ReviewFuture":
        return (
          <ReviewFuture
            ref={reviewFutureRef}
            projectId={projectId}
            reviewDate={reviewDate}
          />
        );
      case "ReportAndDraft":
        return (
          <ReportAndDraft
            projectId={projectId}
            monthlyRateMap={monthlyRateMap}
          />
        );
      case "InvestmentScreeningDetailModal":
        return (
          <Button colorScheme="blue" width={360} onClick={() => { openInvestmentScreeningDetailModal(); }}>{t('features.InvestmentScreening.InvestmentScreeningDetail.label.debugModalButton')}</Button>
        );
      default:
        return <Text>{t('features.InvestmentScreening.InvestmentScreeningDetail.label.selectComponent')}</Text>;
    }
  };

  return (
    <ProjectContext.Provider value={{ userPermissions, reviewDate, isReviewMode, currencyData, currencyStatus }}>
      <Box
        position="fixed"
        top="0"
        left="0"
        width="100vw"
        height="100vh"
        display="flex"
        flexDirection="row"
        zIndex="1000"
        bg="rgba(255, 255, 255, 1)"
      >
        {/* ヘッダーボタン */}
        <Box
          position="absolute"
          top="10px"
          right="40px"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          <HStack spacing={4}>
            {/* 振り返りモードバッジ */}
            {isReviewMode && (
              <Badge colorScheme="green" fontSize="md" px={3} py={1} borderRadius="md">
                {t('features.InvestmentScreening.InvestmentScreeningDetail.label.reviewBadge', {
                  phase: (() => {
                    const currentPhase = reviewList.find(r => r.reviewDate === reviewDate)?.reviewPhase;
                    return currentPhase
                      ? ReviewPhasePresets.find(p => p.value === currentPhase)?.label ?? currentPhase
                      : t('features.InvestmentScreening.InvestmentScreeningDetail.label.reviewDefault');
                  })(),
                  reviewDate,
                })}
              </Badge>
            )}
            {/* ログインユーザー情報 */}
            {employeeId && (
              <Text fontSize="md" fontWeight="bold" color="blue.700" mr={4}>
                {allDivisionNames} - {employeeName}
              </Text>
            )}
            {/* 切り替わるテキスト表示 */}
            {/*
            <Text
              fontSize="lg"
              width="130px"
              fontWeight="bold"
              color={editExistingProject ? "orange.500" : "green.500"}
            >
              {editExistingProject ? "既存案件編集" : "新規案件登録"}
            </Text>
            */}

            {window.name !== SubWindowInvestmentScreeningDetailName ? (
              <>
                {/* ブックマークボタン */}
                {projectId && (
                  <Tooltip label={isBookmarked
                    ? t('features.InvestmentScreening.InvestmentScreeningDetail.label.bookmarkRemove')
                    : t('features.InvestmentScreening.InvestmentScreeningDetail.label.bookmarkAdd')}>
                    <Button
                      h="30px"
                      w="30px"
                      fontSize="xl"
                      variant="ghost"
                      onClick={async () => {
                        if (isBookmarked) {
                          await removeBookmark(projectId, reviewDate);
                          setIsBookmarked(false);
                        } else {
                          const added = await addBookmark(projectId, reviewDate);
                          if (!added) {
                            toast({
                              title: t('features.InvestmentScreening.InvestmentScreeningDetail.label.bookmarkAdd'),
                              description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.bookmarkLimitExceeded', { count: BookmarkMaxCount }),
                              status: "warning",
                              duration: 4000,
                              isClosable: true,
                            });
                            return;
                          }
                          setIsBookmarked(true);
                        }
                      }}
                    >
                      <Icon as={isBookmarked ? FaBookmark : FaRegBookmark} color="#2B6CB0" />
                    </Button>
                  </Tooltip>
                )}
                {/* FAQボタン */}
                <Tooltip label={t('features.faq.label.openFaqInNewTab')}>
                  <Button
                    h="30px"
                    w="30px"
                    fontSize="xl"
                    variant="ghost"
                    onClick={handleOpenFAQ}
                  >
                    <Icon as={RiQuestionnaireLine} color="#2B6CB0" />
                  </Button>
                </Tooltip>
                {/* マニュアルボタン */}
                <Tooltip label={t('features.InvestmentScreening.InvestmentScreeningDetail.label.manual')}>
                  <Button
                    h="30px"
                    w="30px"
                    fontSize="xl"
                    variant="ghost"
                    onClick={onManualModalOpen}
                  >
                    <Icon as={FaBook} color="#2B6CB0" />
                  </Button>
                </Tooltip>

                {/* 一時保存ボタン（対応画面かつ編集可能な場合のみ表示） */}
                {tempSaveRef && isCurrentTabEditable && (
                  <>
                    <Button
                      colorScheme="teal"
                      variant="outline"
                      onClick={() => {
                        const snapshot = tempSaveRef.current?.getSnapshot();
                        const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
                        const json = JSON.stringify({ screenName: activeComponent, savedAt: new Date().toISOString(), data: snapshot }, null, 2);
                        const blob = new Blob([json], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${activeComponent}_${dateStr}.data`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast({ title: activeComponentName, description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.tempSaved'), status: "success", duration: 3000, isClosable: true });
                      }}
                    >
                      {t('features.InvestmentScreening.InvestmentScreeningDetail.button.tempSave')}
                    </Button>
                    <Button
                      colorScheme="teal"
                      variant="outline"
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.accept = ".data";
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            try {
                              const parsed = JSON.parse(ev.target?.result as string);
                              if (parsed.screenName !== activeComponent) {
                                toast({ title: activeComponentName, description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.screenMismatch', { screenName: parsed.screenName }), status: "error", duration: 4000, isClosable: true });
                                return;
                              }
                              const skippedPartNos = tempSaveRef.current?.applySnapshot(parsed.data);
                              // 画面側で PartNo 不一致の警告トーストを表示済みの場合は、読み込み成功トーストと重複表示しない
                              if (!Array.isArray(skippedPartNos) || skippedPartNos.length === 0) {
                                toast({ title: activeComponentName, description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.tempLoaded'), status: "success", duration: 3000, isClosable: true });
                              }
                            } catch {
                              toast({ title: activeComponentName, description: t('features.InvestmentScreening.InvestmentScreeningDetail.message.tempLoadFailed'), status: "error", duration: 4000, isClosable: true });
                            }
                          };
                          reader.readAsText(file);
                        };
                        input.click();
                      }}
                    >
                      {t('features.InvestmentScreening.InvestmentScreeningDetail.button.tempLoad')}
                    </Button>
                  </>
                )}

                {/* 画面閲覧制限を受けるユーザーはコピー不可（コピーすると起案者になり制限が外れるため）。判定が済むまで出さない */}
                {!isReviewMode && !isProjectCopyRestricted(viewRestrictionState) && (
                  <Button
                    colorScheme="blue"
                    isDisabled={!projectId}
                    onClick={(event) => {
                      event.currentTarget.blur();
                      onOpenCopyProjectConfirm();
                    }}
                  >
                    {t('features.InvestmentScreening.InvestmentScreeningDetail.button.copyProject')}
                  </Button>
                )}

                {!isReviewMode && projectId && employeeId === registrantEmployeeId && !isApprovalFinished && (
                  <Button
                    colorScheme="blue"
                    variant="solid"
                    onClick={(event) => {
                      // フォーカスを外す
                      event.currentTarget.blur();
                      onOpen();
                    }}
                  >
                    {t('features.InvestmentScreening.InvestmentScreeningDetail.button.cancel')}
                  </Button>
                )}

                <Button
                  colorScheme="blue"
                  variant="solid"
                  onClick={() => {
                    // 振り返りモード終了時にsessionStorageをクリア
                    if (isReviewMode) {
                      sessionStorage.removeItem("reviewDate");
                    }
                    // 戻るボタン押下
                    navigate("/");
                  }}
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.button.back')}
                </Button>

                <Box marginRight="10px">
                  <Menu />
                </Box>
              </>
            ) : (
              <>
                <Button colorScheme="blue" onClick={() => { window.close(); }}>{t('features.InvestmentScreening.InvestmentScreeningDetail.button.close')}</Button>
              </>
            )}
          </HStack>
        </Box>

        {/* サイドバー */}
        <Box w="250px" bg={isReviewMode ? "green.700" : "blue.700"} color="white" p={4} display="flex" flexDirection="column">
          <VStack spacing={4} align="stretch">
            {/* 振り返りモード: 振り返り切り替えドロップダウン */}
            {reviewList.length > 0 && (
              <Select
                value={reviewDate || (!isReviewMode ? "__application__" : "")}
                onChange={async (e) => {
                  const value = e.target.value;
                  if (value === "__application__") {
                    // 申請データに戻る
                    sessionStorage.removeItem("reviewDate");
                    setReviewDate(null);
                    setViewRestrictionState(undefined);
                    setIsViewRestrictionResolved(false);

                    // データ再取得
                    await fetchData(null);

                    setActiveComponent("");
                    setTimeout(() => setActiveComponent("BasicInfo"), 0);
                  } else {
                    setReviewDate(value);
                  }
                }}
                mb={2}
                bg="rgba(245, 245, 245, 0.2)"
                _hover={{ bg: "rgba(245, 245, 245, 0.4)" }}
              >
                {reviewList.map(r => (
                  <option key={r.reviewDate} value={r.reviewDate} style={{ color: "black" }}>
                    {(r.reviewPhase
                      ? ReviewPhasePresets.find(p => p.value === r.reviewPhase)?.label ?? r.reviewPhase
                      : t('features.InvestmentScreening.InvestmentScreeningDetail.label.reviewDefault'))} ({r.reviewDate})
                  </option>
                ))}
                <option value="__application__" style={{ color: "black" }}>
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.button.applicationDataBack')}
                </option>
              </Select>
            )}
            {/* ========== DEBUG: 月別レート情報（localStorage.DEBUG = "true" のみ表示、テスト用・後で削除） ========== */}
            {isReviewMode && localStorage.getItem("DEBUG") === "true" && (
              <HStack justify="center" spacing={1}>
                <Text fontSize="xs" color="green.200">
                  {monthlyRateMap.size > 0 ? t('features.InvestmentScreening.InvestmentScreeningDetail.label.rateMonths', { count: monthlyRateMap.size }) : t('features.InvestmentScreening.InvestmentScreeningDetail.label.rateLoading')}
                </Text>
                {monthlyRateMap.size > 0 && (
                  <Button size="xs" variant="outline" colorScheme="yellow" onClick={onRateDebugModalOpen}>
                    詳細
                  </Button>
                )}
              </HStack>
            )}
            {/* ========== DEBUG END ========== */}
            {/* 画面メニュー（画面閲覧制限の判定が済むまで表示しない） */}
            {isViewRestrictionResolved && (
              <>
              <Button
                color={activeComponent === "BasicInfo" ? "black" : "white"}
                variant="solid"
                onClick={() => setActiveComponent("BasicInfo")}
                bg={
                  activeComponent === "BasicInfo"
                    ? "#ffffe0"
                    : "rgba(245, 245, 245, 0.2)"
                }
              >
                {t('features.InvestmentScreening.InvestmentScreeningDetail.section.basicInfo')}
              </Button>

              <Button
                color={activeComponent === "QuantityAndPrice" ? "black" : "white"}
                variant="solid"
                onClick={() => setActiveComponent("QuantityAndPrice")}
                bg={
                  activeComponent === "QuantityAndPrice"
                    ? "#ffffe0"
                    : "rgba(245, 245, 245, 0.2)"
                }
              >
                {equipmentUsageType === EquipmentUsageType.GeneralPurpose.code
                  ? t('features.InvestmentScreening.InvestmentScreeningDetail.section.assumedQuantityAndPrice')
                  : t('features.InvestmentScreening.InvestmentScreeningDetail.section.salesQuantityAndPrice')}
              </Button>

              <Button
                color={activeComponent === "PricingAgreementForm" ? "black" : "white"}
                variant="solid"
                onClick={() => setActiveComponent("PricingAgreementForm")}
                bg={
                  activeComponent === "PricingAgreementForm"
                    ? "#ffffe0"
                    : "rgba(245, 245, 245, 0.2)"
                }
              >
                {t('features.InvestmentScreening.InvestmentScreeningDetail.section.pricingAgreement')}
              </Button>
              <Button
                color={activeComponent === "Scheduling" ? "black" : "white"}
                variant="solid"
                onClick={() => setActiveComponent("Scheduling")}
                bg={
                  activeComponent === "Scheduling"
                    ? "#ffffe0"
                    : "rgba(245, 245, 245, 0.2)"
                }
              >
                {t('features.InvestmentScreening.InvestmentScreeningDetail.section.schedule')}
              </Button>
              {!isScreenHidden(WorkflowId.ProductionProcess, viewRestrictionState) && (
                <Button
                  color={activeComponent === "ProductionProcess" ? "black" : "white"}
                  variant="solid"
                  onClick={() => setActiveComponent("ProductionProcess")}
                  bg={
                    activeComponent === "ProductionProcess"
                      ? "#ffffe0"
                      : "rgba(245, 245, 245, 0.2)"
                  }
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.section.productionProcess')}
                </Button>
              )}
              {!isScreenHidden(WorkflowId.Equipment, viewRestrictionState) && (
                <Button
                  color={activeComponent === "Equipment" ? "black" : "white"}
                  variant="solid"
                  onClick={() => setActiveComponent("Equipment")}
                  bg={
                    activeComponent === "Equipment"
                      ? "#ffffe0"
                      : "rgba(245, 245, 245, 0.2)"
                  }
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.section.equipment')}
                </Button>
              )}
              {!isScreenHidden(WorkflowId.ProfitAndLoss, viewRestrictionState) && (
                <Button
                  color={activeComponent === "ProfitAndLoss" ? "black" : "white"}
                  colorScheme="whiteAlpha"
                  variant="solid"
                  onClick={() => setActiveComponent("ProfitAndLoss")}
                  bg={
                    activeComponent === "ProfitAndLoss"
                      ? "#ffffe0"
                      : "rgba(245, 245, 245, 0.2)"
                  }
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.section.cost')}
                </Button>
              )}
              {!isScreenHidden(WorkflowId.ProjectProfitablityStatement, viewRestrictionState) && (
                <Button
                  color={activeComponent === "ProjectProfitablityStatement" ? "black" : "white"}
                  variant="solid"
                  onClick={() => setActiveComponent("ProjectProfitablityStatement")}
                  bg={
                    activeComponent === "ProjectProfitablityStatement"
                      ? "#ffffe0"
                      : "rgba(245, 245, 245, 0.2)"
                  }
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.section.projectProfitability')}
                </Button>
              )}

              {purpose === PurposeType.NewProduct.code && !isScreenHidden(WorkflowId.SimulationNewProduct, viewRestrictionState) &&
                <Button
                  color={activeComponent === "SimulationNewProduct" ? "black" : "white"}
                  variant="solid"
                  onClick={() => setActiveComponent("SimulationNewProduct")}
                  bg={
                    activeComponent === "SimulationNewProduct"
                      ? "#ffffe0"
                      : "rgba(245, 245, 245, 0.2)"
                  }
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.section.simulationNewProduct')}
                </Button>
              }
              {purpose !== PurposeType.NewProduct.code && !isScreenHidden(WorkflowId.SimulationProductivityImprovement, viewRestrictionState) &&
                <Button
                  color={activeComponent === "SimulationProductivityImprovement" ? "black" : "white"}
                  variant="solid"
                  onClick={() => setActiveComponent("SimulationProductivityImprovement")}
                  bg={
                    activeComponent === "SimulationProductivityImprovement"
                      ? "#ffffe0"
                      : "rgba(245, 245, 245, 0.2)"
                  }
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.section.simulationProductivityImprovement')}
                </Button>
              }
              {/*
            <Button colorScheme="whiteAlpha" variant="solid" onClick={() => setActiveComponent("InvestmentScreeningDetailModal")}>
              テスト
            </Button>
  */}
              {isReviewMode && (
                <>
                  <Button
                    color={activeComponent === "ReviewSales" ? "black" : "white"}
                    variant="solid"
                    onClick={() => setActiveComponent("ReviewSales")}
                    bg={
                      activeComponent === "ReviewSales"
                        ? "#ffffe0"
                        : "rgba(245, 245, 245, 0.2)"
                    }
                  >
                    {t('features.InvestmentScreening.InvestmentScreeningDetail.section.reviewSales')}
                  </Button>
                  {!isScreenHidden(WorkflowId.ReviewBusiness, viewRestrictionState) && (
                    <Button
                      color={activeComponent === "ReviewBusiness" ? "black" : "white"}
                      variant="solid"
                      onClick={() => setActiveComponent("ReviewBusiness")}
                      whiteSpace="normal"
                      height="auto"
                      py={2}
                      textAlign="center"
                      bg={
                        activeComponent === "ReviewBusiness"
                          ? "#ffffe0"
                          : "rgba(245, 245, 245, 0.2)"
                      }
                    >
                      {t('features.InvestmentScreening.InvestmentScreeningDetail.section.reviewBusiness')}
                    </Button>
                  )}
                  <Button
                    color={activeComponent === "ReviewFuture" ? "black" : "white"}
                    variant="solid"
                    onClick={() => setActiveComponent("ReviewFuture")}
                    bg={
                      activeComponent === "ReviewFuture"
                        ? "#ffffe0"
                        : "rgba(245, 245, 245, 0.2)"
                    }
                  >
                    {t('features.InvestmentScreening.InvestmentScreeningDetail.section.reviewFuture')}
                  </Button>
                </>
              )}
              {!isScreenHidden(WorkflowId.ReportAndDraft, viewRestrictionState) && (
                <Button
                  color={activeComponent === "ReportAndDraft" ? "black" : "white"}
                  variant="solid"
                  onClick={() => setActiveComponent("ReportAndDraft")}
                  bg={
                    activeComponent === "ReportAndDraft"
                      ? "#ffffe0"
                      : "rgba(245, 245, 245, 0.2)"
                  }
                >
                  {t('features.InvestmentScreening.InvestmentScreeningDetail.section.reportAndDraft')}
                </Button>
              )}
              </>
            )}
          </VStack>
        </Box>

        {/* メインコンテンツ */}
        <Box mt="500px">
          <Box
            bg="white"
            ml="1px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflowY="auto"
          >
            {isCurrencyLoaded && isViewRestrictionResolved ? renderComponent() : null}
          </Box>
        </Box>
        {/* モーダルダイアログ */}
        <InvestmentScreeningDetailModal
          isOpen={isOpenInvestmentScreeningDetailModal} onClose={onCloseInvestmentScreeningDetailModal}
          projectId="c0001-mmkategory10-20250303175018" flowId={WorkflowId.ProductDirector} />
      </Box>

      {/* ========== DEBUG: 月別レート一覧モーダル（テスト用・後で削除） ========== */}
      <Modal isOpen={isRateDebugModalOpen} onClose={onRateDebugModalClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>月別レート一覧（DEBUG）</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={2} color="gray.600">
              取得件数: {monthlyRateMap.size} 月分 / 通貨マスタ: {Array.from(monthlyRateMap.values())[0]?.length ?? 0} 通貨
            </Text>
            <Box maxH="60vh" overflowX="auto" overflowY="auto" border="1px solid" borderColor="gray.200" p={2}>
              {/* 縦=通貨、横=月（期間） */}
              {(() => {
                // 全通貨名を抽出
                const allCurrencies = new Set<string>();
                monthlyRateMap.forEach(rates => rates.forEach(r => { if (r.currencyName) allCurrencies.add(r.currencyName); }));
                const currencyList = Array.from(allCurrencies).sort();
                // 月の昇順キー
                const sortedMonths = Array.from(monthlyRateMap.keys()).sort();

                // 月 → 通貨 → レート のルックアップを事前構築
                const rateLookup: Record<string, Record<string, number>> = {};
                for (const ym of sortedMonths) {
                  rateLookup[ym] = {};
                  for (const r of monthlyRateMap.get(ym) || []) {
                    if (r.currencyName && r.exchangeRate !== undefined) rateLookup[ym][r.currencyName] = r.exchangeRate;
                  }
                }

                return (
                  <Box as="table" fontSize="xs" style={{ borderCollapse: "collapse" }}>
                    <Box as="thead" bg="gray.100" position="sticky" top={0}>
                      <Box as="tr">
                        <Box as="th" p={1} textAlign="left" border="1px solid" borderColor="gray.300" bg="gray.100" position="sticky" left={0} zIndex={1}>
                          通貨 \ 月
                        </Box>
                        {sortedMonths.map(ym => (
                          <Box as="th" key={ym} p={1} textAlign="center" border="1px solid" borderColor="gray.300" whiteSpace="nowrap">
                            {ym}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box as="tbody">
                      {currencyList.map(c => (
                        <Box as="tr" key={c}>
                          <Box as="td" p={1} border="1px solid" borderColor="gray.300" fontWeight="bold" bg="gray.50" position="sticky" left={0} whiteSpace="nowrap">
                            {c}
                          </Box>
                          {sortedMonths.map(ym => (
                            <Box as="td" key={ym} p={1} textAlign="right" border="1px solid" borderColor="gray.300" whiteSpace="nowrap">
                              {rateLookup[ym]?.[c] ?? "-"}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onRateDebugModalClose}>閉じる</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* ========== DEBUG END ========== */}

      {/* 取消確認モーダル */}
      <Modal isOpen={isOpen} onClose={onClose} >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.InvestmentScreeningDetail.modal.cancelConfirmTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              {t('features.InvestmentScreening.InvestmentScreeningDetail.modal.cancelConfirmMessage1')}<br />
              {t('features.InvestmentScreening.InvestmentScreeningDetail.modal.cancelConfirmMessage2')}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onCancel}>
              {t('features.InvestmentScreening.InvestmentScreeningDetail.button.execute')}
            </Button>
            <Button mr={3} onClick={onClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 案件コピー作成確認モーダル */}
      <ConfirmModal
        isOpen={isOpenCopyProjectConfirm}
        title={TITLE}
        message={t('features.InvestmentScreening.InvestmentScreeningDetail.modal.copyProjectConfirmMessage')}
        onOk={() => {
          handleCopyProject();
          onCloseCopyProjectConfirm();
        }}
        onCancel={() => {
          onCloseCopyProjectConfirm();
        }}
      >

      </ConfirmModal>

      {/* マニュアルモーダル */}
      <ManualModal isOpen={isManualModalOpen} onClose={onManualModalClose} />

    </ProjectContext.Provider>
  );
};

export default InvestmentScreeningDetail;

/**
 * 画面パーミッション情報を取得する。
 * @param userPermissions ユーザーパーミッション（確認担当者のdivisionIdを含む）
 * @param pageFlowId ページフローID
 * @param _purpose 目的（未使用、後方互換性のため残存）
 * @param loginUserEmployeeId ログインユーザーの従業員ID
 * @param loginUserDivisionId ログインユーザーの部署ID
 * @param registrantDivisionId 起案者の部署ID
 * @param _loginUserDescendantOrganizationClassIds 未使用、後方互換性のため残存
 * @param _loginUserParentOrganizationId 未使用、後方互換性のため残存
 * @returns 画面パーミッション情報
 */
export const getPermissions = (
  userPermissions: UserPermissionType[],
  pageFlowId: (typeof WorkflowId)[keyof typeof WorkflowId],
  _purpose: string | null | undefined,
  loginUserEmployeeId: string,
  loginUserDivisionId: string,
  registrantDivisionId?: string | null,
  loginUserAffiliations?: Affiliation[],
  approvalStatus?: string
): PagePermissions => {
  const result: PagePermissions = {
    eabledRegistrant: false,
    eabledCompanyDivision: false,
    eabledCompanyHeadDivision: false,
    eabledSalesDivision: false,
    eabledProcurementDivision: false,
    eabledApprovalRequest: false,
    eabledApproval: false,
    eabledPdfDownload: false,
    isEditable: false,
  }

  // フロー区分
  let flowKb: (typeof FlowKb)[keyof typeof FlowKb];
  flowKb = FlowKb.Confirmation;

  // 現在のフローID
  let currentFlowId: (typeof WorkflowId)[keyof typeof WorkflowId];
  currentFlowId = pageFlowId;
  result.currentFlowId = currentFlowId;

  // 画面編集モードフラグ
  let editPermission = false;

  if (userPermissions && userPermissions.length !== 0) {
    // フロー区分が存在する場合 設定
    if (userPermissions[0].flowKb) {
      // フロー区分取得
      flowKb = userPermissions[0].flowKb as (typeof FlowKb)[keyof typeof FlowKb];
    }

    if (userPermissions[0].flowId) {
      // ワークフローID取得（並列承認中はログインユーザーのflow_idを優先）
      const myPermission = userPermissions.find(p => p.employeeId === loginUserEmployeeId);
      currentFlowId = (myPermission?.flowId || userPermissions[0].flowId) as (typeof WorkflowId)[keyof typeof WorkflowId];

      if (flowKb === FlowKb.Approval) {
        // フロー区分が「承認」の場合、ワークフローIDを設定して返す。
        result.currentFlowId = currentFlowId;
      } else {
        // フロー区分が「確認」の場合、画面フローIDを設定して返す。
        result.currentFlowId = pageFlowId;
      }
    }

    if (currentFlowId === WorkflowId.ApproveComplete || currentFlowId === WorkflowId.Cancel) {
      // ワークフローが「承認完了」「取消」の場合、PDFダウンロードのみ
      result.eabledPdfDownload = true;
      return result;
    } else if (currentFlowId === WorkflowId.Remand) {
      // ワークフローが「差戻し」の場合、基本情報のみ編集可(それ以外の画面は編集不可)
      if (pageFlowId === WorkflowId.BasicInfo) {
        result.currentFlowId = currentFlowId;
        result.eabledRegistrant = true;
        result.isEditable = true;
      }

      return result;
    }

    // 確認フローの場合
    if (flowKb === FlowKb.Confirmation) {
      // 画面IDの権限情報を取得
      const filteredPermissions = userPermissions.filter(permission => permission.flowId === pageFlowId);

      // ログイン中ユーザIDが含まれる確認担当者情報を取得
      const userConfirmerPermissions = filteredPermissions.filter(permission => permission.employeeId === loginUserEmployeeId);

      // 起案者の部署と同じ場合は記入者ボタン・申請ボタン・事業部確認ボタンを有効化
      // 複数所属対応：いずれかの所属組織と一致すればOK
      const loginUserOrganizationIds = loginUserAffiliations?.map(a => a.organizationId) || [loginUserDivisionId];
      const isMatchingOrganization = registrantDivisionId && loginUserOrganizationIds.includes(registrantDivisionId);
      if (isMatchingOrganization) {
        result.eabledRegistrant = true;
        result.eabledApprovalRequest = true;
        result.eabledPdfDownload = true;
        result.isEditable = true;
        result.eabledCompanyDivision = true; // 起案者と同じ組織なら事業部確認ボタンも活性
      }

      // 各確認ボタンは、該当divisionIdで登録されているかで判定（組織判定なし）
      userConfirmerPermissions.forEach(permission => {
        if (permission.divisionId === ComfirmDivisions.companyDivision) {
          // 事業部確認（部署を問わず編集可能）
          result.eabledCompanyDivision = true;
          result.eabledPdfDownload = true;
          result.isEditable = true;
        } else if (permission.divisionId === ComfirmDivisions.companyHeadDivision) {
          // 事業管理確認
          result.eabledCompanyHeadDivision = true;
          result.eabledPdfDownload = true;
          result.isEditable = true;
        } else if (permission.divisionId === ComfirmDivisions.salesDivision) {
          // 営業担当者確認
          result.eabledSalesDivision = true;
          result.eabledPdfDownload = true;
          result.isEditable = true;
        } else if (permission.divisionId === ComfirmDivisions.procurementDivision) {
          // 調達担当者確認
          result.eabledProcurementDivision = true;
          result.eabledPdfDownload = true;
          result.isEditable = true;
        }
      });

      // 承認フローの場合
    } else if (flowKb === FlowKb.Approval) {
      // ログイン中ユーザIDが含まれる場合は編集可
      editPermission = userPermissions.some(permission => permission.employeeId === loginUserEmployeeId);

      // 承認ボタン: ユーザーのflow_idが現在のステータスで操作可能かもチェック
      const validStatuses = currentFlowId ? ValidPreviousStatus[currentFlowId] : undefined;
      const isStatusValid = !validStatuses || !approvalStatus || validStatuses.includes(approvalStatus);
      result.eabledApproval = editPermission && isStatusValid;
      result.eabledPdfDownload = editPermission;
    }
  } else if (pageFlowId === WorkflowId.BasicInfo) {
    // 新規作成の場合
    result.eabledRegistrant = true;
    result.isEditable = true;
  }

  return result;
}
